import { CheckIcon } from "@storytell/bsky-icons";
import { PromptStaticRenderer } from "@storytell/rich-text";
import type { StreamingPrompt } from "@storytell/streaming";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { AnimateStaggeredChildren } from "@storytell/ui/animation";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCopy } from "solid-icons/tb";
import { type Component, Show, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Avatar } from "~/components/avatar/Avatar";
import { ScopedItemsList } from "~/components/prompt/internal/components/ScopedItemsList";
import { VariableV1StaticRenderer } from "~/components/prompt/internal/components/VariableV1Renderer/VariableV1Renderer";
import { useCopy } from "~/components/ui";
import { useWire } from "~/wire";

interface PromptAckRendererProps {
  prompt: StreamingPrompt;
  onClick?: () => void;
}

/**
 * Renders prompt acknowledgment blocks - displays the user's prompt with editing capabilities.
 * This is a special renderer that shows the user's avatar and prompt text in a distinct style.
 */
export const PromptAckRenderer: Component<PromptAckRendererProps> = (props) => {
  const wire = useWire();
  const [hovered, setHovered] = createSignal(false);
  const { copied, copy } = useCopy({ timeout: 1000 });

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    await copy(props.prompt.text);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      data-renderer="prompt-ack-renderer"
      class="flex w-full items-start gap-4 py-4 px-6 mb-5 bg-panel hover:bg-panel-hover border border-primary-border rounded-2xl relative group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={props.onClick}
    >
      <div class="flex-1">
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <div class="grid grid-cols-[max-content_max-content_1fr] gap-2 items-center">
              {/* TODO: Get user that submitted the prompt instead of the current user */}
              <Avatar size="2xs" />

              <span class="text-sm text-secondary-text shrink-0">
                {wire.auth.isGuest() ? "You" : wire.auth.user()?.displayName}
              </span>

              <Show
                when={
                  (props.prompt.scope?.assetIdList?.length ?? 0) +
                    (props.prompt.scope?.conceptIdList?.length ?? 0) +
                    (props.prompt.scope?.labelIdList?.length ?? 0) +
                    (props.prompt.scope?.viewIdList?.length ?? 0) >
                  0
                }
              >
                {(() => {
                  const s = wire.sync;
                  const scopeViews = () =>
                    (props.prompt.scope?.viewIdList ?? [])
                      .map((id) => s.views.get(id as ViewId))
                      .filter((v) => v !== undefined) as View[];
                  const scopeLabels = () =>
                    (props.prompt.scope?.labelIdList ?? [])
                      .map((id) => s.labels.get(id as LabelId))
                      .filter((l) => l !== undefined) as Label[];
                  const scopeConcepts = () =>
                    (props.prompt.scope?.conceptIdList ?? [])
                      .map((id) => s.concepts.get(id as ConceptId))
                      .filter((c) => c !== undefined) as Concept[];
                  const scopeAssets = () =>
                    (props.prompt.scope?.assetIdList ?? [])
                      .map((id) => s.assets.get(id as AssetId))
                      .filter((a) => a !== undefined) as Asset[];

                  return (
                    <div class="ml-2 text-xs max-w-full overflow-x-auto">
                      <ScopedItemsList
                        views={scopeViews}
                        labels={scopeLabels}
                        concepts={scopeConcepts}
                        assets={scopeAssets}
                        allowRemove={false}
                      />
                    </div>
                  );
                })()}
              </Show>
            </div>

            <AnimateStaggeredChildren
              show={hovered()}
              enter={{
                opacity: [0, 1],
                y: [-5, 0],
              }}
              stagger={40}
              exit={{
                opacity: [1, 0],
                y: [0, -5],
              }}
            >
              {(animProps) => (
                <div class="relative">
                  <div class="absolute -top-4 -right-2 flex items-center gap-2 text-xs">
                    <StTooltip placement="top" content={<div>{copied() ? "Copied!" : "Copy prompt to clipboard"}</div>}>
                      <StTooltipTrigger
                        ref={animProps.ref}
                        type="button"
                        class="group/btn shadow-md cursor-pointer p-1 rounded-lg bg-tile hover:bg-tile-hover border border-muted-border"
                        onClick={handleCopy}
                      >
                        <Dynamic
                          component={copied() ? CheckIcon : TbCopy}
                          size={16}
                          class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                        />
                      </StTooltipTrigger>
                    </StTooltip>
                  </div>
                </div>
              )}
            </AnimateStaggeredChildren>
          </div>

          <Show
            when={props.prompt.richText}
            fallback={<div class="text-base font-medium text-primary-text">{props.prompt.text}</div>}
          >
            {(doc) => <PromptStaticRenderer doc={doc()} variableV1Renderer={VariableV1StaticRenderer} />}
          </Show>
        </div>
      </div>
    </div>
  );
};
